'use client';

import { ProductGridElementDataType } from '@/components/sections/builder/elements/body/collection-product-grid/type';
import { ElementProps } from '@/components/sections/builder/types';
import { useParams } from 'next/navigation';

import CardProductBuilder from '@/components/sections/builder/elements/body/card-product';
import { PaginationProductgrid } from '@/components/sections/builder/elements/body/collection-product-grid/pagination';
import CardProductSkeleton from '@/components/sections/card-loading';
import { Button } from '@/components/ui/button';
import {
  useProductCategory,
  useProductCollections,
} from '@/hooks/api/storefront/product-collection';
import { SendEventAnalytic, SendEventMetapixel } from '@/lib/analytic-send-event';
import { cn, formatIDR } from '@/lib/utils';
import { IProduct } from '@/types/product';
import Cookies from 'js-cookie';
import React from 'react';
import { usePaginationSearchParams } from '@/hooks/use-pagination';
import { SORTING_QUERY } from '@/constant/data';
import { Star, X } from 'lucide-react';
import { useDebounceEffect, useDebounceValue } from '@/hooks/use-debounce';
import DrawerFilter from '@/components/sections/builder/filter/drawer-filter';
import HorizontalFilter from '@/components/sections/builder/filter/horizontal-filter';
import Sorting from '@/components/sections/builder/filter/sorting';
import VerticalFilter from '@/components/sections/builder/filter/vertical-filter';
import { useSite } from '@/stores/sites';
import { HttpTypes } from '@/types';
import { useStoreSetting } from '@/app/(store)/s/[domain]/store-context';
import { ThemeType } from '@/stores/builder';
import { DEFAULT_THEME_COLOR_SCHEME } from '@/components/sections/builder/theme/color';

export type FilterSortType = {
  availability?: string[];
  categoryName?: string;
  categoryId?: string;
  rating?: number;
};

export default function ElementContent({
  element,
  preview,
  data: dataCollection,
  theme,
}: ElementProps) {
  const eventRef = React.useRef(false);
  const customerToken = Cookies.get('accessToken');
  const data = element.data as ProductGridElementDataType;
  const { storeSetting } = useStoreSetting();

  let collectionData = dataCollection;
  const siteData = useSite((state) => state.data);

  if (preview && siteData) {
    collectionData = siteData as HttpTypes.StoreCollection;
  }

  const { slug: collectionSlug } = useParams<{ slug: string }>();
  const [params, setParams] = usePaginationSearchParams();
  const [filter, setFilter] = React.useState<FilterSortType>({
    availability: [],
    categoryName: '',
    categoryId: '',
    rating: undefined,
  });
  const [sorting, setSorting] = React.useState<string>(
    collectionSlug === 'all' ? 'best-selling' : ''
  );
  const [price, setPrice] = React.useState({ min: '', max: '' });
  const [category, setCategory] = React.useState<string>('');
  const debouncedCategory = useDebounceValue(category, 800);

  const searchParams = {
    slug: collectionSlug,
    page: params.pageIndex + 1,
    pageSize: data.limit || params.pageSize,
    'filter.availability': params.availability.includes('1')
      ? '1'
      : params.availability.includes('0')
      ? '0'
      : [],
    'filter.price.gte': params.minPrice || '',
    'filter.price.lte': params.maxPrice || '',
    'filter.rating': params.rating || (filter.rating ? filter.rating.toString() : ''),
    sortBy: collectionSlug !== 'all' ? params.sortBy || sorting : params.sortBy || 'best_selling',
    sortDirection:
      collectionSlug !== 'all'
        ? params.sortDirection || 'DESC'
        : (params.sortDirection || 'desc').toLowerCase(),
    categoryId: params.categoryId || '',
  };

  const { data: productData, isLoading } = useProductCollections(
    searchParams,
    customerToken as string
  );
  const { data: categories, isLoading: isLoadingCategory } = useProductCategory({
    q: debouncedCategory,
    slug: collectionSlug !== 'all' ? collectionSlug : '',
  });

  const products = productData?.data || [];
  const totalPages = Math.ceil(Number(productData?.totalCount) / Number(searchParams.pageSize));

  const updateParams = (newParams: Partial<typeof params>) => {
    setParams((prev) => ({ ...prev, ...newParams }));
  };

  const handlePageChange = (newPage: number) => updateParams({ pageIndex: newPage - 1 });

  const toggleAvailability = (type: 'inStock' | 'outStock') => {
    const checkedValue = type === 'inStock' ? '1' : '0';

    updateParams({
      pageIndex: 0,
      availability: params.availability?.[0] === checkedValue ? [] : [checkedValue],
    });
    setFilter((prev) => ({
      ...prev,
      availability: prev.availability?.[0] === checkedValue ? [] : [checkedValue],
    }));
  };

  const handlePriceChange = (type: 'min' | 'max', value: string) => {
    if (!/^\d*$/.test(value)) return;

    setPrice((prevPrice) => ({
      ...prevPrice,
      [type]: value,
    }));
  };

  useDebounceEffect(() => updateParams({ minPrice: price.min, maxPrice: price.max }), 500, [price]);

  const handleSortByChange = (sort: string) => {
    const findSorting = SORTING_QUERY.find((item) => item.value === sort);
    if (!findSorting) return;
    updateParams({
      pageIndex: 0,
      sortBy: findSorting.sortBy,
      sortDirection: findSorting.sortDirection,
    });
    setSorting(sort);
  };

  const handleSelectCategory = (id: string, name: string) => {
    updateParams({ pageIndex: 0, categoryId: id });
    setFilter((prev) => ({ ...prev, categoryId: id, categoryName: name }));
  };

  const handleRatingFilter = (ratingValue: number) => {
    const newRating = filter.rating === ratingValue ? undefined : ratingValue;
    updateParams({ pageIndex: 0, rating: newRating?.toString() }); // CONVERT to string for URL
    setFilter((prev) => ({ ...prev, rating: newRating }));
  };

  const handleRemoveFilter = (name: string) => {
    switch (name) {
      case 'inStock':
      case 'outStock':
        setFilter((prev) => ({ ...prev, availability: [] }));
        updateParams({ availability: [] });
        break;
      case 'price':
        setPrice({ min: '', max: '' });
        updateParams({ minPrice: '', maxPrice: '' });
        break;
      case 'category':
        setFilter((prev) => ({ ...prev, categoryId: '' }));
        updateParams({ categoryId: '' });
        break;
      case 'rating':
        setFilter((prev) => ({ ...prev, rating: undefined }));
        updateParams({ rating: undefined });
        break;
      default:
        break;
    }
  };

  const handleResetFilter = () => {
    setSorting('best-selling');
    setPrice({ min: '', max: '' });
    setFilter({ availability: [], categoryId: '' });
    updateParams({
      pageIndex: 0,
      sortBy: '',
      sortDirection: '',
      minPrice: '',
      maxPrice: '',
      availability: [],
      categoryId: '',
      rating: undefined,
    });
  };

  React.useEffect(() => {
    if (collectionData) {
      setSorting(collectionData.sorting);
    }
  }, []);

  React.useEffect(() => {
    const { categoryId, availability, sortBy, sortDirection, minPrice, maxPrice, rating } = params;

    setFilter((prev) => {
      let newFilter = {
        availability,
        categoryId: '',
        categoryName: '',
        rating: rating ? Number(rating) : undefined,
      };

      if (categoryId && categories?.data) {
        const findCategory = categories.data.find((cat) => cat.category_id === categoryId);
        newFilter = {
          availability,
          categoryId,
          categoryName: findCategory?.category_name || '',
          // rating,
          rating: rating ? Number(rating) : undefined,
        };
      }

      return JSON.stringify(prev) !== JSON.stringify(newFilter) ? newFilter : prev;
    });

    if (sortBy) {
      const findSorting = SORTING_QUERY.find(
        (item) => item.sortBy === sortBy && item.sortDirection === sortDirection
      );
      if (findSorting) {
        setSorting((prev) => (prev !== findSorting.value ? findSorting.value : prev));
      }
    }

    setPrice((prev) => {
      const newPrice = { min: minPrice, max: maxPrice };
      return JSON.stringify(prev) !== JSON.stringify(newPrice) ? newPrice : prev;
    });
  }, [params]);

  React.useEffect(() => {
    if (!eventRef.current && !preview && collectionData) {
      SendEventAnalytic('view_item_list', {
        item_list_id: collectionData?.collection_id as string,
        item_list_name: collectionData?.collection_name as string,
        items: products.map((product: IProduct) => ({
          item_id: product.id || '',
          item_name: product?.product_name || '',
        })),
      });
      SendEventMetapixel('ViewContent', {
        content_ids: products.map((product: IProduct) => product.id),
        content_type: 'product',
        contents: products.map((product: IProduct) => ({
          id: product.id,
          quantity: product.qty,
        })),
        currency: 'IDR',
      });

      eventRef.current = true;
    }
  }, [collectionData, preview]);

  const isActiveReviewProduct = storeSetting?.product_settings?.review_product ?? false;

  const starColor = theme?.productRating?.starColor || DEFAULT_THEME_COLOR_SCHEME.text;

  return (
    <>
      <section className='bg_product_grid_color_scheme py-2'>
        <div
          className='theme_layout_page_width mx-auto w-full duration-100'
          style={{
            paddingTop: `${data.padding?.top || 0}px`,
            paddingBottom: `${data.padding?.bottom || 0}px`,
            paddingLeft: `${data.padding?.left || 0}px`,
            paddingRight: `${data.padding?.right || 0}px`,
          }}
        >
          {data.showProductNameCount !== false && (
            <div className='w-full items-center gap-2 px-5'>
              <h1
                className={cn(
                  'theme_font_heading text-lg md:text-2xl lg:text-3xl',
                  data.filterLayout === 'vertical' && 'lg:hidden'
                )}
              >
                {collectionSlug === 'all' ? 'All Products' : collectionData?.collection_name || ''}{' '}
                ({productData?.totalCount as number})
              </h1>
            </div>
          )}
          <div className='flex w-full items-center justify-between px-4'>
            {data.enableFilter && (
              <div
                className={cn(
                  'flex items-center gap-2',
                  data.filterLayout === 'vertical' && 'lg:hidden',
                  data.filterLayout === 'horizontal' && 'lg:hidden'
                )}
              >
                <DrawerFilter
                  categories={categories?.data || []}
                  data={data}
                  totalProducts={productData?.totalCount as number}
                  filter={filter}
                  price={price}
                  updateSortBy={handleSortByChange}
                  currentSorting={sorting}
                  setCategory={setCategory}
                  handleSelectCategory={handleSelectCategory}
                  handleCheckedChange={toggleAvailability}
                  handlePriceChange={handlePriceChange}
                  handleRemoveFilter={handleRemoveFilter}
                  handleResetFilter={handleResetFilter}
                  isLoadingCategory={isLoadingCategory}
                  handleRatingFilter={handleRatingFilter}
                  isActiveReviewProduct={isActiveReviewProduct}
                  isPreview={preview}
                  theme={theme as ThemeType}
                />
              </div>
            )}
            {data.enableFilter && data.filterLayout === 'horizontal' && (
              <div className='hidden lg:block'>
                <HorizontalFilter
                  data={data}
                  categories={categories?.data || []}
                  filter={filter}
                  price={price}
                  setCategory={setCategory}
                  handleSelectCategory={handleSelectCategory}
                  handleResetFilter={handleResetFilter}
                  handleCheckedChange={toggleAvailability}
                  handlePriceChange={handlePriceChange}
                  isLoadingCategory={isLoadingCategory}
                  handleRatingFilter={handleRatingFilter}
                  isActiveReviewProduct={isActiveReviewProduct}
                  isPreview={preview}
                  theme={theme as ThemeType}
                />
              </div>
            )}
            <div
              className={cn(
                'hidden w-full items-center gap-2',
                data.filterLayout === 'vertical' && 'lg:flex'
              )}
            >
              {data.showProductNameCount !== false && (
                <h1 className='theme_font_heading text-lg md:text-2xl lg:text-3xl'>
                  {collectionSlug === 'all'
                    ? 'All Products'
                    : collectionData?.collection_name || ''}{' '}
                  ({productData?.totalCount as number})
                </h1>
              )}
            </div>
            <div
              className={cn(
                'flex items-center gap-2',
                data.filterLayout === 'vertical' && 'w-full justify-end'
              )}
            >
              {data.enableSort && (
                <div className='hidden sm:flex'>
                  <Sorting
                    updateSortBy={handleSortByChange}
                    currentSorting={params.sorting || sorting}
                  />
                </div>
              )}
            </div>
          </div>
          <div
            className={cn(
              'product-list-padding relative flex w-full',
              products.length === 0 && 'flex-col lg:flex-row'
            )}
          >
            {data.enableFilter && data.filterLayout === 'vertical' && (
              <div className='hidden w-full max-w-[300px] flex-col gap-3 pl-3 pt-3 lg:block'>
                <VerticalFilter
                  handleRatingFilter={handleRatingFilter}
                  data={data}
                  categories={categories?.data || []}
                  price={price}
                  filter={filter}
                  setCategory={setCategory}
                  handleCheckedChange={toggleAvailability}
                  handlePriceChange={handlePriceChange}
                  handleSelectCategory={handleSelectCategory}
                  handleRemoveFilter={handleRemoveFilter}
                  handleResetFilter={handleResetFilter}
                  isLoadingCategory={isLoadingCategory}
                  isActiveReviewProduct={isActiveReviewProduct}
                  isPreview={preview}
                  theme={theme as ThemeType}
                />
              </div>
            )}
            {data.overlay && <div className={cn('overlay absolute inset-0')} />}
            <div className='relative flex h-full w-full items-center justify-center px-3 py-3 lg:px-6 lg:py-6'>
              <div
                className={cn(
                  'flex w-full flex-col',
                  data.contentAlign === 'left' && '[&>*]:text-left',
                  data.contentAlign === 'center' && '[&>*]:text-center',
                  data.contentAlign === 'right' && '[&>*]:text-right'
                )}
              >
                {(data.filterLayout === 'horizontal' || data.filterLayout === 'drawer') && (
                  <div className={cn('mb-2 hidden flex-1 md:flex')}>
                    <div className='flex flex-col gap-3 md:flex-row'>
                      {data.enableFilter && filter.categoryId && (
                        <div className='md:mb-2'>
                          <Button
                            variant='outline'
                            className='items-center rounded-full text-xs lg:text-sm'
                            onClick={() => handleRemoveFilter('category')}
                          >
                            <span>{filter.categoryName}</span>
                            <X className='ml-2 h-3 w-3' />
                          </Button>
                        </div>
                      )}
                      {data.enableFilter && filter.availability?.includes('1') && (
                        <div className='md:mb-2'>
                          <Button
                            variant='outline'
                            className='items-center rounded-full text-xs lg:text-sm'
                            onClick={() => handleRemoveFilter('inStock')}
                          >
                            <span>Availability: In stock</span>
                            <X className='ml-2 h-3 w-3' />
                          </Button>
                        </div>
                      )}
                      {data.enableFilter && filter.availability?.includes('0') && (
                        <div className='md:mb-2'>
                          <Button
                            variant='outline'
                            className='items-center rounded-full text-xs lg:text-sm'
                            onClick={() => handleRemoveFilter('outStock')}
                          >
                            <span>Availability: Out of stock</span>
                            <X className='ml-2 h-3 w-3' />
                          </Button>
                        </div>
                      )}
                      {price.min && price.max && (
                        <div className='md:mb-2'>
                          <Button
                            variant='outline'
                            className='items-center rounded-full text-xs lg:text-sm'
                            onClick={() => handleRemoveFilter('price')}
                          >
                            {formatIDR(price.min)} - {formatIDR(price.max)}{' '}
                            <X className='ml-2 h-3 w-3' />
                          </Button>
                        </div>
                      )}
                      {filter.rating && (
                        <div>
                          <Button
                            variant='outline'
                            className='inline-flex items-center gap-x-1 gap-y-2 rounded-full text-xs md:text-sm'
                            onClick={() => handleRemoveFilter('rating')}
                          >
                            <span>Rating:</span>
                            <Star
                              className='h-4 w-4 '
                              style={{
                                color: starColor,
                                fill: starColor,
                              }}
                            />
                            <span className=''>{filter.rating}</span>
                            <X className='ml-2 h-3 w-3' />
                          </Button>
                        </div>
                      )}
                    </div>
                    {(filter.availability?.length || price.min || price.max || filter.rating) &&
                      data.enableFilter && (
                        <div className='flex h-full w-full'>
                          <Button
                            variant='link'
                            className='text-xs underline lg:text-sm'
                            onClick={handleResetFilter}
                            style={{ color: data.colorScheme?.btnSolidBackgroud }}
                          >
                            Remove all
                          </Button>
                        </div>
                      )}
                  </div>
                )}
                <div className='h-full w-full'>
                  {isLoading ? (
                    <div className='grid grid-cols-2 gap-3 md:grid-cols-4 lg:grid-cols-4'>
                      {Array.from({ length: 4 }).map((_, index) => (
                        <CardProductSkeleton key={index} />
                      ))}
                    </div>
                  ) : products.length > 0 ? (
                    <div
                      className={cn(
                        `theme_layout_space_grid_horizontal theme_layout_space_grid_vertical grid h-full w-full place-content-center`,
                        products.length > 0
                          ? `grid-cols-${data.totalColMobile} md:grid-cols-${data.totalCol} lg:grid-cols-${data.totalCol}`
                          : 'flex'
                      )}
                    >
                      {products.map((product) => (
                        <CardProductBuilder
                          data={data}
                          product={product}
                          key={product.id}
                          element={element}
                          preview={preview}
                          theme={theme}
                        />
                      ))}
                    </div>
                  ) : (
                    <div className='w-full flex-col text-center'>
                      <h2 className='my-16 text-base font-light text-black lg:text-2xl'>
                        No products found <br />
                        Use fewer filters or{' '}
                        <Button
                          variant='link'
                          className='-ml-3 text-base font-light underline hover:decoration-[3px] lg:text-2xl'
                          onClick={handleResetFilter}
                          style={{ color: data.colorScheme?.btnSolidBackgroud }}
                        >
                          remove all
                        </Button>
                      </h2>
                    </div>
                  )}
                  {totalPages > 1 && !isLoading ? (
                    <PaginationProductgrid
                      handlePageChange={handlePageChange}
                      page={params.pageIndex + 1}
                      totalPages={totalPages}
                    />
                  ) : null}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
      <style jsx>
        {`
          @media (min-width: 768px) {
            .full_width_product_list {
              width: calc(100vw - 20px);
              margin-left: calc(-50vw + 50% + 10px);
            }
          }
          .card-rounded {
            border-radius: ${data.rounded || 0}px;
          }
          .overlay {
            background-color: ${data.overlay};
          }
          .bg_product_grid_color_scheme {
            background-color: ${data.colorScheme?.background || 'white'};
            color: ${data.colorScheme?.text || '52535B'};
          }
          .background-color {
            background-color: ${data.background?.backgroundColor};
            --tw-bg-opacity: ${data.background?.backgroundOpacity};
          }
          .background-image {
            background-image: url(${data.background?.backgroundImage});
            background-repeat: no-repeat;
            background-size: ${data.background?.backgroundSize};
            background-position: ${data.background?.backgroundPosition};
            background-attachment: ${data.background?.backgroundAttachment};
          }
        `}
      </style>
    </>
  );
}
